import ObjectId from 'bson-objectid';
import { Button, Col, Dropdown, Input, Row, Text, TextEditor } from 'components';
import { Api, sports } from 'lib';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Image, Tags } from 'screens/Admin/components';
import styled from 'styled-components';

const StyledDropdown = styled(Dropdown)`
  p  {
    margin: 0 19px;
  }
`;

const imageSizes = ['big', 'med', 'small'];

const AddArticle = () => {
  const [title, setTitle] = useState(''),
    [preTitle, setPreTitle] = useState(''),
    [subTitle, setSubTitle] = useState(''),
    [sport, setSport] = useState(''),
    [mainImage, setMainImage] = useState(null),
    [secondaryImage, setSecondaryImage] = useState(null),
    [editImages, setEditImages] = useState(null),
    [body, setBody] = useState(''),
    [tags, setTags] = useState([]),
    isMobile = useSelector((state) => state.appState.isMobile),
    navigate = useNavigate(),
    { articleId } = useParams(),
    { state } = useLocation();

  useEffect(() => {
    if (!articleId) return;
    if (state) return fetchEditingData(state);

    fetchEditingData();
  }, []);

  async function fetchEditingData(data) {
    if (!data) {
      const res = await Api.get(`/articles/getOne/${articleId}`);
      if (!res) return;

      data = res.article;
    }

    setTitle(data.title);
    setSubTitle(data.subTitle);
    setBody(data.body);
    setTags(data.tags);
    setSport(data.sport);
    setPreTitle(data.title);

    const mainImages = Object.keys(data.images)
      .filter((x) => x.includes('main'))
      .map((x) => data.images[x]);

    const secondaryImages = Object.keys(data.images)
      .filter((x) => x.includes('secondary'))
      .map((x) => data.images[x]);

    setEditImages({
      main: { edited: false, data: mainImages },
      secondary: { edited: false, data: secondaryImages }
    });
  }

  function handleEditedImages(data, type) {
    let currObj = editImages;
    editImages[type] = { edited: true, data };

    setEditImages(currObj);
  }

  function removeImage(type) {
    let currObj = editImages;
    editImages[type] = { edited: true, data: [] };

    setEditImages(currObj);
  }

  async function submit() {
    if (!title || !subTitle || !sport || !body || !tags.length || !mainImage || !secondaryImage) {
      return alert('All fields are required!');
    }

    const documentId = ObjectId().toString();

    let data = new FormData(),
      fileNames = [];

    data.append('title', title);
    data.append('subTitle', subTitle);
    data.append('sport', sport);
    data.append('body', body);
    data.append('tags', JSON.stringify(tags));
    data.append('documentId', documentId);

    mainImage.forEach((x, i) => {
      const fileName = `${documentId}-main-${imageSizes[i]}.jpeg`;
      fileNames.push(fileName);

      data.append('images[]', x, fileName);
    });

    secondaryImage.forEach((x, i) => {
      const fileName = `${documentId}-secondary-${imageSizes[i]}.jpeg`;
      fileNames.push(fileName);

      data.append('images[]', x, fileName);
    });

    data.append('fileNames', JSON.stringify(fileNames));

    const res = await Api.post('/admin/addNewsArticle', data, 'addNewsArticle');
    if (!res) return console.log('Something went wrong!');

    navigate('/admin/articles');
  }

  async function edit() {
    const { main, secondary } = editImages;
    const mainImgEdited = (main.edited && main.data.length) || !main.edited;
    const secondaryImgEdited = (secondary.edited && secondary.data.length) || !secondary.edited;

    if (
      !title ||
      !subTitle ||
      !sport ||
      !body ||
      !tags.length ||
      !mainImgEdited ||
      !secondaryImgEdited
    ) {
      return alert('All fields are required!');
    }

    let data = new FormData(),
      fileNames = [];

    data.append('_id', articleId);
    data.append('title', title);
    data.append('subTitle', subTitle);
    data.append('sport', sport);
    data.append('body', body);
    data.append('tags', JSON.stringify(tags));

    if (main.edited) {
      main.data.forEach((x, i) => {
        const fileName = `${articleId}-main-${imageSizes[i]}.jpeg`;
        fileNames.push(fileName);

        data.append('images[]', x, fileName);
      });
    }

    if (secondary.edited) {
      secondary.data.forEach((x, i) => {
        const fileName = `${articleId}-secondary-${imageSizes[i]}.jpeg`;
        fileNames.push(fileName);

        data.append('images[]', x, fileName);
      });
    }

    data.append('fileNames', JSON.stringify(fileNames));

    const res = await Api.post('/admin/editNewsArticle', data, 'addNewsArticle');
    if (!res) return console.log('Something went wrong!');

    navigate('/admin/articles');
  }

  if (isMobile) {
    return (
      <Col center pad="20px" ht="100vh">
        <Text h1 ta="center">
          Please visit this screen on a computer browser.
        </Text>
      </Col>
    );
  }

  // TODO: ADD SPINNER
  if (articleId && !title) return null;

  return (
    <Col wid="100%">
      <Col ht="100%" wid="1140px" pad="50px 20px" center>
        <Col center>
          {articleId ? (
            <Text size="30px" marg="0 0 50px" med>
              EDIT {preTitle.toUpperCase()}
            </Text>
          ) : (
            <Text size="30px" marg="0 0 50px" med>
              ADD NEWS ARTICLE
            </Text>
          )}

          <Input
            onChange={({ target }) => setTitle(target.value)}
            value={title}
            marg="0 0 20px"
            placeholder="Title"
            admin
          />

          <Input
            onChange={({ target }) => setSubTitle(target.value)}
            marg="0 0 20px"
            value={subTitle}
            placeholder="Sub title"
            admin
          />

          <StyledDropdown
            admin
            handleSelect={(e) => setSport(e.value)}
            options={sports}
            title="Sport"
            marg="0 0 20px"
            selected={sport}
            website
            admin
            left
          />

          <Tags definedTags={tags} handleChange={(data) => setTags(data)} />

          <Row start marg="0 0 20px">
            <Col minWid="250px" marg="0 100px 0 0">
              <Text med size="22px">
                Main image
              </Text>

              <Image
                removeImage={() => removeImage('main')}
                imgs={editImages?.main || mainImage}
                setImgs={(data, type) =>
                  articleId ? handleEditedImages(data, type) : setMainImage(data)
                }
                type="main"
              />
            </Col>

            <Col minWid="250px">
              <Text med size="22px">
                Secondary image
              </Text>

              <Image
                removeImage={() => removeImage('secondary')}
                imgs={editImages?.secondary || secondaryImage}
                setImgs={(data, type) =>
                  articleId ? handleEditedImages(data, type) : setSecondaryImage(data)
                }
                type="secondary"
              />
            </Col>
          </Row>

          <TextEditor onChange={(val) => setBody(val)} value={body} />

          <Col center>
            <Button
              text={articleId ? 'Edit' : 'Publish'}
              website
              marg="20px 0 0"
              onClick={articleId ? edit : submit}
              action="addNewsArticle"
            />
          </Col>
        </Col>
      </Col>
    </Col>
  );
};

export default AddArticle;
